import { useEffect, useRef } from 'react';
import type React from 'react';
import { useNavigate } from 'react-router';

import { useLogger } from '../../core/services/telemetry/useLogger';
import useNavigationStore from '../../core/stores/navigationStore';
import { resolvePasNavigation } from './utils/resolvePasNavigation';

// Only the shape of an unrecognised detail is logged, never its values: it comes from another package.
const describeUnrecognised = (detail: unknown) => {
  const { to, planCode } = (typeof detail === 'object' && detail !== null ? detail : {}) as {
    to?: unknown;
    planCode?: unknown;
  };
  return {
    to: typeof to === 'string' ? to.slice(0, 40) : typeof to,
    planCodeType: typeof planCode,
  };
};

/** Routes the `pasnavigate` event that `b2o-pas-widget` raises from anywhere inside it. */
const PasNavigationBoundary = ({ children }: { children: React.ReactNode }) => {
  const navigate = useNavigate();
  const { hasPendingChanges, blockNavigation } = useNavigationStore();
  const logger = useLogger();
  const loggerRef = useRef(logger);
  loggerRef.current = logger;
  const ref = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const element = ref.current;
    if (!element) return undefined;

    const handle = (event: Event): void => {
      const { detail } = event as CustomEvent;
      const route = resolvePasNavigation(detail);
      if (route === null) {
        loggerRef.current.warn(
          'PAS: unrecognised pasnavigate detail ignored',
          describeUnrecognised(detail),
        );
        return;
      }
      if (hasPendingChanges) blockNavigation(route);
      else navigate(route);
    };

    element.addEventListener('pasnavigate', handle);
    return (): void => element.removeEventListener('pasnavigate', handle);
  }, [navigate, hasPendingChanges, blockNavigation]);

  return (
    <div ref={ref} style={{ display: 'contents' }}>
      {children}
    </div>
  );
};

export default PasNavigationBoundary;
